import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  ChartOptions,
  ChartData,
  Filler,
} from 'chart.js';
import { LEGACY_COLORS, INTERVAL_OPTIONS } from '../../../constants/graphColors';
import analyseTranslation from '../../../constants/AnalyseTranslation.json';
import AnalyseQualityHistoryInterval from './AnalyseQualityHistoryInterval';
import { ChartAnalysisItem } from '../../../../types/analyses';

ChartJS.register(LineElement, PointElement, LinearScale, Filler);

type AnalyseQualityHistoryProps = {
  analysesHistory: ChartAnalysisItem[];
};

const findDateInArray = (date: Date, data: ChartAnalysisItem[]): ChartAnalysisItem | undefined =>
  data.find(
    singleData => new Date(singleData.created_on).toDateString() === new Date(date).toDateString()
  );

const getDateDaysAgo = (days: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};

const emptyDays = (dateDataFirst: string, dateDaysAgo: Date): boolean =>
  new Date(dateDataFirst) > new Date(dateDaysAgo);

const getDaysTilToday = (day: string): number =>
  Math.round((new Date().getTime() - new Date(day).getTime()) / (1000 * 60 * 60 * 24));

const getNextDayInOrder = (day: string): Date => {
  const d = new Date(day);
  d.setDate(d.getDate() + 1);
  return d;
};

const manipulateObject = (obj: Partial<ChartAnalysisItem>, date: string): ChartAnalysisItem => ({
  item_error_count: obj.item_error_count || 0,
  item_warning_count: obj.item_warning_count || 0,
  item_count: obj.item_count || 0,
  item_info_count: obj.item_info_count || 0,
  progress_score: obj.progress_score || 0,
  ...obj,
  created_on: date,
});

const emptyObj: Partial<ChartAnalysisItem> = {
  item_error_count: 0,
  item_warning_count: 0,
  item_count: 0,
  item_info_count: 0,
  progress_score: 0,
};

// we get data from the backend for all days, items got modified, but for our graph we need data for each day for the given interval
// fakeArray is a array with faked data: missing dates get added and holds the state of the last given date
const generateFakeArray = (data: ChartAnalysisItem[], days: number): ChartAnalysisItem[] => {
  // get back the first day in our days interval
  const dateDaysAgo = getDateDaysAgo(days);

  // filter out all data which is older than our firstDate in the interval
  let dataArrayAfterDay = data.filter(d => d.created_on > dateDaysAgo.toISOString());

  // in case the time interval is to low
  if (!dataArrayAfterDay.length) {
    dataArrayAfterDay = data;
  }

  // check how many days it is from our first data til today
  const daysTilToday = getDaysTilToday(dataArrayAfterDay[0].created_on);

  // if the interval is greater than our first given data, daysNoData hold the days for how many days there are no data
  // so empty data Objects can be added for each day
  let daysNoData = -1;
  if (emptyDays(data[0].created_on, dateDaysAgo)) {
    daysNoData = days - daysTilToday;
  }

  let nextDayInOrder: any = dateDaysAgo; // starts with day 0 and counts up
  let lastGivenDay = dataArrayAfterDay[0]; // holds the date of the last real day
  const fakeArray: ChartAnalysisItem[] = [];

  for (let i = 0; i < days + 1; i++) {
    const isDateGiven = findDateInArray(nextDayInOrder, dataArrayAfterDay);
    if (i < daysNoData) {
      // as long there is no data the array gets empty data
      fakeArray.push(manipulateObject(emptyObj, new Date(nextDayInOrder).toISOString()));
    } else if (isDateGiven) {
      // if a date in our data is found lastGivenDay is updated
      fakeArray.push(isDateGiven);
      lastGivenDay = isDateGiven;
    } else {
      // the array gets fake data - the data from the previous day
      const fakeObjWithNewDate = manipulateObject(
        lastGivenDay,
        new Date(nextDayInOrder).toISOString()
      );
      fakeArray.push(fakeObjWithNewDate);
    }
    nextDayInOrder = getNextDayInOrder(nextDayInOrder);
  }

  return fakeArray;
};

const generateLineData = (array: ChartAnalysisItem[]): ChartData<'line'> => {
  const labels = array.map(d => new Date(d.created_on).toDateString().slice(4, 10)); // get a nice string for output
  const lastItemCount = array[array.length - 1];
  const itemCount = array.map(d => d.item_count);
  const progress = array.map(d => (d.progress_score * lastItemCount.item_count) / 100);
  const errors = array.map(d => d.item_error_count);
  const warnings = array.map(d => d.item_warning_count);
  const infos = array.map(d => d.item_info_count);

  return {
    labels,
    datasets: [
      {
        label: 'Total count',
        fill: false,
        cubicInterpolationMode: 'monotone',
        pointBackgroundColor: 'white',
        backgroundColor: LEGACY_COLORS.blackOpacity,
        borderColor: LEGACY_COLORS.blackOpacity,
        borderJoinStyle: 'miter',
        pointBorderWidth: 1.5,
        pointHoverRadius: 2,
        pointRadius: 2,
        data: itemCount,
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'With Errors',
        fill: false,
        cubicInterpolationMode: 'monotone',
        backgroundColor: LEGACY_COLORS.redOpacity,
        borderColor: LEGACY_COLORS.redOpacity,
        borderJoinStyle: 'miter',
        pointBorderWidth: 0,
        pointHoverRadius: 0,
        pointRadius: 0,
        data: errors,
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'With Warnings',
        fill: false,
        cubicInterpolationMode: 'monotone',
        backgroundColor: LEGACY_COLORS.yellowOpacity,
        borderColor: LEGACY_COLORS.yellowOpacity,
        borderJoinStyle: 'miter',
        pointBorderWidth: 0.7,
        pointHoverRadius: 0,
        pointRadius: 0,
        data: warnings,
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'With Recommendations',
        fill: false,
        cubicInterpolationMode: 'monotone',
        backgroundColor: LEGACY_COLORS.blueOpacity,
        borderColor: LEGACY_COLORS.blueOpacity,
        borderJoinStyle: 'miter',
        pointBorderWidth: 0.7,
        pointHoverRadius: 0,
        pointRadius: 0,
        data: infos,
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'Progress',
        fill: {
          target: 'origin',
        },
        cubicInterpolationMode: 'monotone',
        backgroundColor: LEGACY_COLORS.lightGreenOpacity,
        borderColor: LEGACY_COLORS.lightGreenOpacity,
        borderJoinStyle: 'miter',
        pointBorderWidth: 0.7,
        pointHoverRadius: 0,
        pointRadius: 0,
        data: progress,
        borderWidth: 0.7,
        tension: 0.4,
      },
    ],
  };
};

const generateOptions = (array: ChartAnalysisItem[]): ChartOptions<'line'> => {
  const progressScore = array.map(d => d.progress_score);
  const maxItemCount = array.map(d => d.item_count).reduce((a, b) => Math.max(a, b));
  const maxItemCountRounded = Math.ceil(maxItemCount / 5) * 5;
  const steps = maxItemCountRounded / 5;
  return {
    responsive: false,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      tooltip: {
        position: 'nearest',
        callbacks: {
          label: context => {
            // datasetIndex 4 is progress
            if (context.datasetIndex === 4) {
              return `Average Progress: ${progressScore[context.dataIndex]} %`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
    scales: {
      x: {
        ticks: {
          color: LEGACY_COLORS.blackOpacity,
          maxRotation: 0,
        },
        grid: {
          display: false,
          offset: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          offset: false,
        },
        offset: true,
        beginAtZero: true,
        min: 0,
        max: maxItemCountRounded || 100,
        ticks: {
          stepSize: steps || 20,
        },
      },
    },
  };
};

const AnalyseQualityHistory: React.FC<AnalyseQualityHistoryProps> = ({ analysesHistory }) => {
  const [dayRange, setDayRange] = useState(14);

  const handleDateRangeChange = (days: number) => {
    setDayRange(days);
  };

  const checkIfAnalysesExist = () => {
    if (analysesHistory.length === 0) {
      return (
        <span className="analyses-not-existing">{analyseTranslation.no_analyses_existing}</span>
      );
    }
    return null;
  };

  let data: ChartData<'line'> = generateLineData([]);
  let allTimeDays: number | undefined;
  let options: ChartOptions<'line'> = generateOptions([{}] as ChartAnalysisItem[]);

  if (analysesHistory && analysesHistory.length > 0) {
    const fakeArray = generateFakeArray(analysesHistory, dayRange);
    data = generateLineData(fakeArray);
    allTimeDays = getDaysTilToday(analysesHistory[0].created_on);
    options = generateOptions(fakeArray);
  }

  return (
    <div className="analyse-quality-history analyse__border">
      <div className="analyse-quality-history__top flex">
        <p className="title home-container__analyse-title">
          {analyseTranslation.quality_history}
          <Tooltip title={analyseTranslation.tooltip.quality_history}>
            <InfoCircleOutlined className="ml-1" />
          </Tooltip>
          {checkIfAnalysesExist()}
        </p>
        <AnalyseQualityHistoryInterval
          className="analyse-quality-history__canvas"
          handleDateRangeChange={handleDateRangeChange}
          allTimeDays={allTimeDays}
        />
      </div>
      <Line data={data} options={options} height={240} width={910} />
    </div>
  );
};

export default AnalyseQualityHistory;
